import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type { OrderIssue } from '../types/orderIssues';

type SortDirection = 'desc' | 'asc';

export function useProcessingQueue(statusFilter: 'under_process' | 'processed' | 'resolved') {
  const { user } = useAuth();
  const adminId = getAdminId(user);

  const [issues, setIssues] = useState<OrderIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const loadIssues = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_open_order_issues', {
        p_admin_id: adminId,
        p_search: search || null,
        p_issue_type_filter: null,
        p_status_filter: statusFilter,
        p_limit: pageSize,
        p_offset: page * pageSize,
        p_sort_direction: sortDirection,
      });

      if (error) throw error;
      const rows = (data || []) as OrderIssue[];
      setIssues(rows);
      setTotal(rows.length > 0 ? Number(rows[0].total_count) || 0 : 0);
    } catch {
      setIssues([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminId, statusFilter, search, sortDirection, page, pageSize]);

  const markAsProcessed = useCallback(
    async (issueIds: string[]) => {
      if (!adminId || issueIds.length === 0) return;
      try {
        const { error } = await supabase.rpc('mark_issues_processed', {
          p_admin_id: adminId,
          p_issue_ids: issueIds,
          p_processed_by: user?.id ?? adminId,
        });
        if (error) throw error;
        await loadIssues();
      } catch (err) {
        console.error('Failed to mark issues as processed:', err);
      }
    },
    [adminId, user?.id, loadIssues]
  );

  const resolveIssues = useCallback(
    async (issueIds: string[], note?: string) => {
      if (!adminId || issueIds.length === 0) return;
      try {
        const { error } = await supabase.rpc('bulk_resolve_order_issues', {
          p_admin_id: adminId,
          p_issue_ids: issueIds,
          p_resolved_by: user?.id ?? adminId,
          p_resolution_note: note ?? null,
        });
        if (error) throw error;
        await loadIssues();
      } catch (err) {
        console.error('Failed to resolve issues:', err);
      }
    },
    [adminId, user?.id, loadIssues]
  );

  const updateSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
  }, []);

  const toggleSort = useCallback(() => {
    setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setPage(0);
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  return {
    issues,
    loading,
    total,
    page,
    pageSize,
    search,
    sortDirection,
    setPage,
    updateSearch,
    toggleSort,
    loadIssues,
    markAsProcessed,
    resolveIssues,
  };
}
